import { eik } from "@borealis/image";
import { i18n } from "@lingui/core";
import { Button } from "@warp-ds/react";
import errorImageUrl from "../../assets/error.svg";
import { ErrorState as ErrorStateType } from "../stores/searchStore";

type ErrorStateProps = {
	className?: string;
	error: ErrorStateType;
	onRetry?: () => void;
	helpCenterUrl: string;
};

const errorSrc: string = eik(errorImageUrl);

export function ErrorState(props: ErrorStateProps) {
	const { className = "", error, onRetry, helpCenterUrl } = props;

	const getErrorContent = () => {
		if (error.errorType === "server" || (error.statusCode && error.statusCode >= 500)) {
			return {
				heading: i18n.t({
					id: "explore.results.errorstate.server.heading",
					message: "Sorry!",
				}),
				message: i18n.t({
					id: "explore.results.errorstate.server.message",
					message: "Explore isn’t working right now, but we’re on it. Please try again later!",
				}),
				showRetry: false,
				illustration: errorSrc,
			};
		}

		if (error.errorType === "client" || (error.statusCode && error.statusCode >= 400 && error.statusCode < 500)) {
			return {
				heading: i18n.t({
					id: "explore.results.errorstate.client.heading",
					message: "Oh no!",
				}),
				message: i18n.t({
					id: "explore.results.errorstate.client.message",
					message: "Something went wrong, and we couldn’t load any items. Please try again!",
				}),
				showRetry: true,
				illustration: errorSrc,
			};
		}

		// Network error
		return {
			heading: i18n.t({
				id: "explore.results.errorstate.network.heading",
				message: "Connection Error",
			}),
			message: i18n.t({
				id: "explore.results.errorstate.network.message",
				message: "We couldn't reach our servers. Please check your internet connection and try again.",
			}),
			showRetry: true,
			illustration: errorSrc,
		};
	};

	const content = getErrorContent();

	const handleRetry = () => {
		if (onRetry) {
			onRetry();
		} else {
			// Default retry behavior - reload the page
			window.location.reload();
		}
	};

	const goBack = () => {
		// Redirect to the marketplace search page

		const goBackLabel = i18n.t({
			id: "explore.results.errorstate.goback.label",
			message: "Go back to search on recommerce",
		});

		if (content.showRetry) {
			return (
				<Button secondary type="button" href="/recommerce/forsale/search" aria-label={goBackLabel} className="mt-16">
					{goBackLabel}
				</Button>
			);
		} else {
			return (
				<Button primary type="button" href="/recommerce/forsale/search" aria-label={goBackLabel} className="mt-16">
					{goBackLabel}
				</Button>
			);
		}
	};

	const helpCenter = () => {
		if (!helpCenterUrl) {
			console.error("Help center URL is missing - help button will not be displayed");
			return null;
		}
		return (
			<Button
				secondary
				quiet
				type="button"
				href={helpCenterUrl}
				aria-label={i18n.t({
					id: "explore.results.errorstate.help.center.label",
					message: "Visit our help center",
				})}
				className="mt-48"
			>
				{i18n.t({
					id: "explore.results.errorstate.help.center.label",
					message: "Visit our help center",
				})}
			</Button>
		);
	};

	return (
		<div className={`${className} flex flex-col items-center justify-center w-full`}>
			<div className="flex flex-col items-center md:w-1/2 sm:w-full justify-center">
				<img className="p-16" src={content.illustration} alt={content.heading} />
				<h2 className="t2 text-center mb-8">{content.heading}</h2>

				<p className="text-body text-center mb-24">{content.message}</p>

				{content.showRetry && (
					<Button
						primary
						type="button"
						aria-label={i18n.t({
							id: "explore.results.errorstate.retry.label",
							message: "Try again",
						})}
						onClick={handleRetry}
					>
						{i18n.t({
							id: "explore.results.errorstate.retry.label",
							message: "Try again",
						})}
					</Button>
				)}
				{goBack()}
				{helpCenter()}
			</div>
		</div>
	);
}
